import { authorizedFetch, API_ORIGIN } from '@yeshuman/sdk'
import { useQuery } from '@tanstack/react-query'

export type MarketplaceInterestPeriod = '7d' | '30d'

export type MarketplaceInterestRow = {
  id: string
  candidate_name: string
  candidate_email: string
  opportunity_id: string
  job_title: string
  provider: string
  note: string
  has_note: boolean
  created_at: string
  job_path: string
}

export type MarketplaceInterestList = {
  period: string
  total: number
  limit: number
  offset: number
  providers: string[]
  results: MarketplaceInterestRow[]
}

export type MarketplaceInterestFilters = {
  period: MarketplaceInterestPeriod
  provider: string
  q: string
  hasNote: 'any' | 'yes' | 'no'
  offset?: number
  limit?: number
}

function buildQuery(filters: MarketplaceInterestFilters): string {
  const params = new URLSearchParams()
  params.set('period', filters.period)
  if (filters.provider) params.set('provider', filters.provider)
  if (filters.q.trim()) params.set('q', filters.q.trim())
  if (filters.hasNote === 'yes') params.set('has_note', 'true')
  if (filters.hasNote === 'no') params.set('has_note', 'false')
  params.set('limit', String(filters.limit ?? 50))
  params.set('offset', String(filters.offset ?? 0))
  return params.toString()
}

export function useMarketplaceInterests(filters: MarketplaceInterestFilters) {
  const qs = buildQuery(filters)
  return useQuery({
    queryKey: ['talentco', 'marketplace-interests', qs],
    queryFn: async (): Promise<MarketplaceInterestList> => {
      const response = await authorizedFetch(
        `${API_ORIGIN}/api/talentco/marketplace-interests?${qs}`,
      )
      const body = await response.json()
      if (!response.ok) {
        const error = new Error(
          (body as { error?: string; detail?: string }).error
            || (body as { detail?: string }).detail
            || `Failed to load marketplace interests (${response.status})`,
        )
        ;(error as Error & { status?: number }).status = response.status
        throw error
      }
      return body as MarketplaceInterestList
    },
  })
}
